// src/pages/knowledgeBase/TypeBaselinesPanel.jsx
//
// Knowledge Base sub-section listing the seeded Type Baseline registers
// — read-only for now. Registers are produced by curated seeding
// (seeds/registers/*.json + scripts/seedTypeBaselineRegisters.mjs);
// future CRUD will live on top of risk_register_type_baseline directly.

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Stack,
  Group,
  Title,
  Text,
  Card,
  Badge,
  Button,
  Alert,
  Table,
} from '@mantine/core';
import { IconRefresh, IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import { authFetch } from '../../utils/authFetch';
import { isRiskManager } from '../../utils/roleHelpers';

// Display order for the recognised model-type / overlay pairs. Anything not
// listed here still renders, just appended in the natural sort order returned
// by the API.
const PROTOTYPES = [
  { model_type: 'llm', overlay_slug: null, label: 'LLM', sub: 'Common risks' },
  { model_type: 'llm', overlay_slug: 'closed-weight', label: 'LLM · Closed-weight', sub: 'API-hosted overlay' },
  { model_type: 'llm', overlay_slug: 'open-weight', label: 'LLM · Open-weight', sub: 'Self-hosted overlay' },
  { model_type: 'multimodal', overlay_slug: null, label: 'Multimodal Foundation', sub: 'Text + vision' },
  { model_type: 'generative-vision-audio', overlay_slug: null, label: 'Generative Vision/Audio', sub: 'Image / video / voice generation' },
  { model_type: 'predictive-ml', overlay_slug: null, label: 'Predictive ML', sub: 'Classification / regression / forecasting' },
  { model_type: 'predictive-ml', overlay_slug: 'tabular', label: 'Predictive ML · Tabular', sub: 'Tabular overlay' },
  { model_type: 'recommender', overlay_slug: null, label: 'Recommender / Ranking', sub: 'Ranking + ad targeting' },
  { model_type: 'speech', overlay_slug: null, label: 'Speech (ASR/TTS)', sub: 'Recognition + synthesis' },
  { model_type: 'code-generation', overlay_slug: null, label: 'Code Generation', sub: 'Inline + agent codegen' },
];

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function TypeBaselinesPanel() {
  const { user } = useAuth0();
  const userIsRiskManager = useMemo(() => {
    const userRoles =
      user?.[`${window.location.origin}/roles`] ||
      user?.['https://agenthuginn.ai/roles'] ||
      user?.['https://riskgpt.app/roles'] ||
      [];
    return isRiskManager(userRoles);
  }, [user]);

  const [baselines, setBaselines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/.netlify/functions/listTypeBaselines');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBaselines(data.baselines || []);
    } catch (err) {
      console.error('[TypeBaselinesPanel] load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const baselineByKey = useMemo(() => {
    const map = new Map();
    for (const b of baselines) {
      const key = `${b.model_type}|${b.overlay_slug || ''}`;
      map.set(key, b);
    }
    return map;
  }, [baselines]);

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={4}>Baseline Registers</Title>
          <Text size="sm" c="dimmed">
            Reusable baseline risk registers per AI model type, structured per ISO 31000
            (source · event · consequence). Curated by risk managers; consumed as RAG
            context by Builder and Explorer assessments.
          </Text>
        </Stack>
        <Group gap="xs">
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            onClick={load}
            loading={loading}
          >
            Refresh
          </Button>
          {userIsRiskManager && (
            <Button
              size="xs"
              disabled
              title="Registers editor (CRUD on risk_register_type_baseline + ISO 31000 risk fields) lands in a follow-up. Today's content is curated via seeds/registers/*.json and applied with scripts/seedTypeBaselineRegisters.mjs."
            >
              Edit
            </Button>
          )}
        </Group>
      </Group>

      {!userIsRiskManager && (
        <Alert color="blue" icon={<IconAlertCircle size={16} />} variant="light">
          Read-only browsing. The Registers Editor (manager-only CRUD) lands in a follow-up.
        </Alert>
      )}

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>Failed to load: {error}</Alert>
      )}

      <Card withBorder p={0}>
        <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Model type / overlay</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Last seeded</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {PROTOTYPES.map((p) => {
              const key = `${p.model_type}|${p.overlay_slug || ''}`;
              const b = baselineByKey.get(key);
              return (
                <Table.Tr key={key}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text fw={600}>{p.label}</Text>
                      <Text size="xs" c="dimmed">
                        <code>{p.model_type}{p.overlay_slug ? ` · ${p.overlay_slug}` : ''}</code>
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{p.sub}</Text>
                  </Table.Td>
                  <Table.Td>
                    {b ? (
                      <Badge color="blue" variant="light" leftSection={<IconCircleCheck size={12} />}>
                        Seeded · v{b.version}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light">Not yet seeded</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{formatTimestamp(b?.generated_at)}</Text>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
